import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '../../Components/ui/button'
import { Skeleton } from '../../Components/ui/skeleton'
import { Spinner } from '../../Components/ui/spinner'
import env from '../../env/env'
import { get } from '../../http/restservice'
import InvestorProfile from './InvestorProfile'
import QuestionnaireV2Profile from './QuestionnaireV2Profile'
import {
  ADMIN_BASE_PATH,
  buildQuestionnaireV2Report,
  buildQuestionnaireV2View,
  downloadBlob,
  downloadUserResponseCsv,
  isQuestionnaireV2,
  removeCountryCode,
  unwrapUserResponse,
} from './firstUserIdeaUtils'

const FirstUserIdeaProfile = () => {
  const { phoneNumber } = useParams()
  const location = useLocation()
  const selectedUserFromList = location.state?.selectedUser || null

  const [userRes, setUserRes] = useState(null)
  const [singleUser, setSingleUser] = useState(selectedUserFromList)
  const [isLoading, setIsLoading] = useState(true)

  const decodedPhone = phoneNumber ? decodeURIComponent(phoneNumber) : ''

  useEffect(() => {
    let cancelled = false

    const fetchUserDetails = async () => {
      if (!decodedPhone) {
        setIsLoading(false)
        return
      }

      setIsLoading(true)
      try {
        const modifiedPhoneNumber = removeCountryCode(decodedPhone)
        const response = await get(
          `${env.API_URL}adminservice/api/actionPlan/fetchFirstIdeas?phoneNumber=${modifiedPhoneNumber}`,
        )

        if (cancelled) return

        if (response?.isError) {
          throw new Error(response.message || 'Unable to load user response')
        }

        let record = response
        if (typeof response?.data === 'string') {
          try {
            record = JSON.parse(response.data)
          } catch {
            record = response?.data ?? response
          }
        } else {
          record = response?.data ?? response
          if (typeof record === 'string') {
            try {
              record = JSON.parse(record)
            } catch {
              record = null
            }
          }
        }

        const rawResponse =
          record?.userResponse ?? record?.data?.userResponse ?? record?.data ?? record
        setUserRes(rawResponse ?? null)
        setSingleUser(
          record?.phoneNumber || record?.firstName || record?.lastName
            ? record
            : { ...(selectedUserFromList || {}), ...(record || {}) },
        )
      } catch (error) {
        if (!cancelled) {
          toast.error('Unable to load user response')
          setUserRes(null)
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    fetchUserDetails()
    return () => {
      cancelled = true
    }
  }, [decodedPhone, selectedUserFromList])

  const selectedUser = singleUser || selectedUserFromList
  const payload = useMemo(() => unwrapUserResponse(userRes), [userRes])
  const isV2 = isQuestionnaireV2(payload)
  const hasResponse = userRes !== null && userRes !== undefined && userRes !== ''
  const v1Answers = useMemo(() => {
    if (isV2 || payload == null) return []
    if (Array.isArray(payload)) return payload
    if (typeof payload === 'object') {
      return Object.entries(payload).map(([key, value]) => ({ [key]: value }))
    }
    return []
  }, [isV2, payload])

  const v2View = useMemo(() => {
    if (!isV2) return null
    return buildQuestionnaireV2View({
      data: payload,
      selectedUser,
      singleUser,
    })
  }, [isV2, payload, selectedUser, singleUser])

  const handleDownloadCsv = () => {
    if (!decodedPhone || !hasResponse) {
      toast.error('No user selected or response data is empty')
      return
    }

    downloadUserResponseCsv({
      selectedInvoice: decodedPhone,
      userRes,
      users: selectedUser ? [selectedUser] : [],
      singleUser,
    })
  }

  const handleDownloadReport = () => {
    if (!isV2 || !v2View) return
    downloadBlob(
      buildQuestionnaireV2Report(v2View),
      `${v2View.fullName.replace(/\s+/g, '_').toLowerCase()}_questionnaire_v2.txt`,
      'text/plain;charset=utf-8',
    )
  }

  return (
    <div className='mx-auto w-full max-w-none px-4 pb-8 pt-4 md:px-6 lg:px-8'>
      <div className='mb-5 flex flex-col gap-4 md:mb-6'>
        <Button variant='ghost' size='sm' className='w-fit rounded-xl px-2' asChild>
          <Link to={`${ADMIN_BASE_PATH}/firstUserIdeas`}>
            <ArrowLeft className='h-4 w-4' />
            Back to responses
          </Link>
        </Button>
      </div>

      {isLoading ? (
        <div className='flex flex-col gap-4'>
          <div className='flex min-h-[120px] items-center justify-center gap-2 text-muted-foreground'>
            <Spinner className='h-5 w-5' />
            Loading profile…
          </div>
          <Skeleton className='h-28 w-full' />
          <div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
            <Skeleton className='h-64 w-full' />
            <Skeleton className='h-64 w-full' />
          </div>
        </div>
      ) : isV2 && v2View ? (
        <QuestionnaireV2Profile
          view={v2View}
          onDownloadCsv={handleDownloadCsv}
          onDownloadReport={handleDownloadReport}
          csvDisabled={!hasResponse}
        />
      ) : v1Answers.length > 0 ? (
        <InvestorProfile
          userRes={v1Answers}
          selectedUser={selectedUser}
          singleUser={singleUser}
          onDownloadCsv={handleDownloadCsv}
          csvDisabled={!hasResponse}
        />
      ) : (
        <p className='py-16 text-center text-sm text-muted-foreground'>No response data found.</p>
      )}
    </div>
  )
}

export default FirstUserIdeaProfile
